import globalAxios from "axios";


/**
 * Thrown when user passes a value to retryType that doesn't belong to the enum
 */
class UnrecognisedRetryTypeError extends Error {
  constructor(retryType) {
    super(`Unrecognised retryType "${retryType}"`)
  }
}


/**
 * getDelayedPromise - returns a promise that resolves after delayTimeout
 *
 * @param  {Number} delayTimeout
 * @returns {Promise}
 */
function getDelayedPromise(delayTimeout) {
  return new Promise( (resolve,reject) => {
    setTimeout( () => {
       resolve()
     }, delayTimeout
    );
  })
}



/**
 * AxiosRetryInterceptor -
 */
export default class AxiosRetryInterceptor {

  /**
   * Enum for special retryTypes
   */
  static retryType = {
    EXPONENTIAL: "retryType/EXPONENTIAL",
    LINEAR: "retryType/LINEAR",
    STATIC: "retryType/STATIC"
  }

  /**
   * @static getDefaultConfig - get default config object for merging over
   *
   * @returns {Object}
   */
  static getDefaultConfig() {
    return {
      maxRetries:3,
      retryType:AxiosRetryInterceptor.retryType.EXPONENTIAL,
      retryValue:null,
      retryHTTPStatusRanges:[
        [100,199],
        [429,429],
        [500,599]
      ],
      onBeforeRetry: (error)=>{},
      onRetry: (error)=>{}
    }
  }

  static errorHasHTTPResponse(error) {
    return error.response !== undefined && error.response.status !== undefined;
  }

  static getRetryConfig(error) {
    if (error.config !== undefined && error.config.retryConfig !== undefined) {
      return error.config.retryConfig;
    }
    return false;
  }

  /**
   * @static getNextRetryIndex
   *
   * @param  {Object} retryConfig
   * @returns {Number}
   */
  static getNextRetryIndex(retryConfig) {
    return retryConfig.currentRetryIndex + 1 || 1;
  }

  static getNextRetryDelay(retryConfig) {
    const nextRetryIndex = AxiosRetryInterceptor.getNextRetryIndex(retryConfig);
    switch(retryConfig.retryType) {
      // return exp backoff timeout
      case AxiosRetryInterceptor.retryType.EXPONENTIAL:
        return (( Math.pow(3, (nextRetryIndex) ) -1 )/2) * 1000;

      case AxiosRetryInterceptor.retryType.LINEAR:
        return ( nextRetryIndex * retryConfig.retryValue )

      case AxiosRetryInterceptor.retryType.STATIC:
        return retryConfig.retryValue;
      default:
        throw new UnrecognisedRetryTypeError(retryConfig.retryType)

    }
  }

  static isStatusInRanges(statusCode, validRanges) {
    for(const range of validRanges) {
      const [min, max] = range;
      if (statusCode >= min && statusCode <= max) {
        return true;
      }
    }
  }

  static shouldRetryError(error) {
    const retryConfig = AxiosRetryInterceptor.getRetryConfig(error);
    if ( retryConfig === false ) {
      return false;
    }

    // If the error has a response, and the response has a status code, and that status code is permanent, no retry
    if( AxiosRetryInterceptor.errorHasHTTPResponse(error) ) {
      if (AxiosRetryInterceptor.isStatusInRanges(error.response.status, retryConfig.retryHTTPStatusRanges) ) {
        return false;
      }
    }

    // If the next retry index would leave us over our maxRetries, no retry
    if(AxiosRetryInterceptor.getNextRetryIndex(retryConfig) > retryConfig.maxRetries) {
      return false;
    }

    // Otherwise, assume retryable.
    return true;
  }

  onError = (error) => {
    // No Retries allowed, reject
    if ( AxiosRetryInterceptor.shouldRetryError(error) === false ) {
      return Promise.reject(error);
    }

    const nextRetryDelay = AxiosRetryInterceptor.getNextRetryDelay(error.config.retryConfig);
    error.config.retryConfig = {
      ...error.config.retryConfig,
      currentRetryIndex:  AxiosRetryInterceptor.getNextRetryIndex(error.config.retryConfig),
      currentWaitTimeout: nextRetryDelay,
      totalWaitTimeout: error.config.retryConfig.totalWaitTimeout + nextRetryDelay
    }

    error.config.retryConfig.onBeforeRetry(error);

    // After nextRetryDelay, resolve the promise with another promise of the next retry axios promise
    return getDelayedPromise(nextRetryDelay).then( () => {
      // Call user Handler (after wait timeout)
      error.config.retryConfig.onRetry(error);
      return this.axiosInstance(error.config)
    })

  }

  // take the instanceDefaults and merge the specific request's config over it
  onRequest = (requestConfig) => {
      const nextRequestConfig = {
        ...requestConfig,
        retryConfig: {
          ...AxiosRetryInterceptor.getDefaultConfig(),
          // Initialise counter values
          currentRetryIndex: 0,
          currentWaitTimeout: 0,
          totalWaitTimeout: 0,
          ...requestConfig.retryConfig
        }
      }
      requestConfig.retryConfig = {

      }
      return nextRequestConfig;
    }

  detatch = () => {
    this.axiosInstance.interceptors.request.eject(this.requestInterceptorID);
    this.axiosInstance.interceptors.response.eject(this.responseInterceptorID);
  }

  constructor(axiosInstance = globalAxios) {
    this.axiosInstance = axiosInstance;
    this.requestInterceptorID  = axiosInstance.interceptors.request.use(this.onRequest );
    this.responseInterceptorID = axiosInstance.interceptors.response.use(null, this.onError );
  }
}



// WEBPACK FOOTER //
// ./src/lib/ajax/retry.js